import { useEffect, useState } from "react";

import { generatePath, Redirect, useParams } from "react-router-dom";

import { postTrackingData } from "@/api/endpoints/track";
import fetcher_v2 from "@/api/fetcher_v2";
import { URLS } from "@/api/urls";
import ArrowRight from "@/assets/icons/arrow-right.svg";
import { useAlert } from "@/contexts/AlertContext";
import { useTags } from "@/contexts/TagContext";
import { ROUTES } from "@/routes_new";
import { cx } from "@/utils";
import Clickable from "@components/Clickable";
import Typography from "@components/Typography";

import NewReportModal from "./NewReportModal";

import styles from "./NewReportView.module.css";

// future TS: {color: "pink" |"purple" | "blue"}
const ReportTemplateLink = ({ children, color = "purple", ...props }) => {
  return (
    <Clickable
      {...props}
      className={cx(styles.card, {
        [styles.purple]: color === "purple",
        [styles.pink]: color === "pink",
        [styles.blue]: color === "blue",
      })}
    >
      <div>{children}</div>
      <span className={styles.createButton}>
        <ArrowRight height={18} />
      </span>
    </Clickable>
  );
};

const NewReportView = (props) => {
  const [templateName, setTemplateName] = useState(null);

  const { orgUuid } = useParams();
  const { groupedAlerts } = useAlert();
  const { organizationTags } = useTags();

  // picked alerts and/or tags
  const [alerts, setAlerts] = useState([]);
  const [tags, setTags] = useState([]);

  // only for time reports
  const [interval, _setInterval] = useState("monthly");
  const [isValidated, setIsValidated] = useState(false);
  const [redirectToUrl, setRedirectToUrl] = useState(null);

  const [createReportLoading, setCreateReportLoading] = useState(false);

  useEffect(() => {
    if (templateName === "blank") {
      <Redirect to={generatePath(ROUTES.REPORTS_NEW_ORGID, { orgUuid })} />;
    }
  }, [templateName, orgUuid]);

  const alertToSourceOption = (source) => {
    const isLoading = source.status === "initializing";

    return {
      value: source.id,
      label:
        source.title +
        (isLoading
          ? ` (${L.REPORTS_WIDGET_SOURCES_ALERTS_LOADING_SUFFIX}) `
          : ""),
      uuid: source.uuid,
      type: "search",
      isDisabled: isLoading,
    };
  };

  const activeAlertOptions = groupedAlerts.active?.map(alertToSourceOption);
  const expiredAlertOptions = groupedAlerts.inactive?.map(alertToSourceOption);
  const savedMentionsOptions = groupedAlerts.saved?.map(alertToSourceOption);

  // A bit un-DRY, but we want to place "favorites" first (as in the menu-bar)
  const regularTags = organizationTags.filter(
    (tag) => tag.name !== "favorited"
  );
  const favoriteTag = organizationTags.filter(
    (tag) => tag.name === "favorited"
  );

  const regularTagOptions = (regularTags || []).map((tag) => ({
    value: tag.slug,
    label: tag.name,
    type: "tag",
  }));

  const favoriteTagOption = (favoriteTag || []).map((tag) => ({
    value: tag.slug,
    label: L.REPORTS_WIDGET_SOURCES_FAVORITE_OPTION,
    type: "tag",
  }));

  const allTagOptions = favoriteTagOption.concat(regularTagOptions);

  const sourceOptions = [
    {
      label: L.REPORTS_WIDGET_SOURCES_ACTIVE_ALERTS_GROUP_HEADER,
      options: activeAlertOptions,
    },
    {
      label: L.REPORTS_WIDGET_SOURCES_EXPIRED_ALERTS_GROUP_HEADER,
      options: expiredAlertOptions,
    },
    {
      label: L.REPORTS_WIDGET_SOURCES_TAGS_GROUP_HEADER,
      options: allTagOptions,
    },
    {
      label: L.REPORTS_WIDGET_SOURCES_SAVED_MENTIONS_GROUP_HEADER,
      options: savedMentionsOptions,
    },
  ];

  const handleHide = () => {
    setTemplateName(null);
    setAlerts([]);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const validateForm = (form) => {
    setIsValidated(true);

    const valid = form.checkValidity();
    const minimumSourceAmount = templateName === "comparison" ? 2 : 1;
    const sourcesSelect = document.getElementById("sourcesSelect");
    if (sourcesSelect) {
      if (alerts.length + tags.length < minimumSourceAmount) {
        sourcesSelect.classList.add("is-invalid");
        return false;
      } else {
        sourcesSelect.classList.add("is-valid");
        return valid;
      }
    }
  };

  const handleSave = (form) => {
    if (!validateForm(form)) return;
    setCreateReportLoading(true);

    const data = {
      template: templateName,
      alert_sources: alerts.map((a) => a.uuid),
      tag_sources: tags.map((t) => t["slug"]),
    };

    if (templateName === "time_comparison") {
      data["interval"] = interval;
    }

    fetcher_v2(URLS.REPORT_LIST, {
      method: "POST",
      body: data,
    })
      .then(({ data }) => {
        setCreateReportLoading(false);
        setRedirectToUrl(
          generatePath(ROUTES.REPORTS_EDIT_ORGID, { orgUuid, uuid: data.uuid })
        );
      })
      .catch((error) => {
        setCreateReportLoading(false);
        throw error; // let sentry handle it
      });
  };

  if (redirectToUrl) {
    return <Redirect to={redirectToUrl} />;
  }

  return (
    <div className={props.className}>
      <div className={styles.cards}>
        <ReportTemplateLink
          onClick={() => {
            setTemplateName("overview");
            postTrackingData("Click on new report template link", {
              templateName: "overview",
            });
          }}
          data-testid="new_report_overview"
        >
          <Typography variant="h4">
            {L.REPORTS_CREATE_OVERVIEW_BUTTON}
          </Typography>

          <Typography className="description">
            {L.REPORTS_CREATE_OVERVIEW_DESCRIPTION}
          </Typography>
        </ReportTemplateLink>

        <ReportTemplateLink
          color="pink"
          onClick={() => {
            setTemplateName("comparison");
            postTrackingData("Click on new report template link", {
              templateName: "comparison",
            });
          }}
          data-testid="new_report_comparison"
        >
          <Typography variant="h4">
            {L.REPORTS_CREATE_COMPARISON_BUTTON}
          </Typography>

          <Typography className="description">
            {L.REPORTS_CREATE_COMPARISON_DESCRIPTION}
          </Typography>
        </ReportTemplateLink>

        <ReportTemplateLink
          color="blue"
          onClick={() => {
            setTemplateName("time_comparison");
            postTrackingData("Click on new report template link", {
              templateName: "time_comparison",
            });
          }}
          data-testid="new_report_time_comparison"
        >
          <Typography variant="h4">
            {L.REPORTS_CREATE_TIME_COMPARISON_BUTTON}
          </Typography>

          <Typography className="description">
            {L.REPORTS_CREATE_TIME_COMPARISON_DESCRIPTION}
          </Typography>
        </ReportTemplateLink>
      </div>

      <NewReportModal
        handleHide={handleHide}
        handleSave={handleSave}
        handleSubmit={handleSubmit}
        templateName={templateName}
        sourceOptions={sourceOptions}
        interval={interval}
        setInterval={_setInterval}
        isValidated={isValidated}
        alerts={alerts}
        setAlerts={setAlerts}
        tags={tags}
        setTags={setTags}
        createReportLoading={createReportLoading}
      />
    </div>
  );
};

export { NewReportView };
